import React from 'react';
import cx from 'clsx';
import { useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { History } from 'history';
import { TFunction } from 'i18next';
import { ParsedQuery } from 'query-string';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import GET_SUPPORT_TOPICS from 'graph/support/support-topics-query';
import GET_ALL_SUPPORT_CATEGORIES from 'graph/support/all-support-categories-query';
import GET_SUPPORT_CATEGORY from 'graph/support/support-category-query';

import CategoryHeader from './CategoryHeader';
import ContactUsPanel from 'components/@common/ContactUsPanel';

import { ModuleProps } from 'types';
import { parseQueryParams } from 'utilities/query-params-util';
import PageContainer from '../../@layout/PageContainer';
import SupportFilterBar from './SupportFilterBar';
import SupportHeader from './SupportHeader';
import { SupportTopic, SupportCategory } from 'types/schema-generated-types';
import SupportTile from './SupportTile';
import SupportTileGrid from './SupportTileGrid';
import { SupportView } from '../types';
import TopicDetails from '../modals/TopicDetails';
import useStore from 'store';
import ViewMoreButton from './ViewMoreButton';
import useRouteAction from 'hooks/route-action/use-route-action';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import HeaderBar from '../HeaderBar';
import { buildSearchQueryParams } from 'utilities/query-params-util';
import config from 'config';

export interface SupportLayoutProps {
  view: Omit<SupportView, 'search'>;
}

const getQuery = (type: SupportView): DocumentNode => {
  switch (type) {
    case SupportView.CATEGORY:
      return GET_ALL_SUPPORT_CATEGORIES;
    case SupportView.TOPIC:
      return GET_SUPPORT_CATEGORY;
    case SupportView.SEARCH:
      return GET_SUPPORT_TOPICS;
    default:
      return GET_ALL_SUPPORT_CATEGORIES;
  }
};

const getVariables = (
  queryParams?: ParsedQuery<string> | null,
  categoryId?: string,
  view?: SupportView
): object => {
  if (queryParams) {
    return {
      variables: {
        where: {
          productName: queryParams?.product,
          search: queryParams?.search,
          ...(categoryId && {
            supportCategoryId: +categoryId
          })
        }
      }
    };
  }
  if (view === SupportView.TOPIC && categoryId) {
    return { variables: { where: { id: +categoryId } } };
  }
  return {};
};

const sortBySortIndex = (
  a: SupportTopic | SupportCategory,
  b: SupportCategory | SupportTopic
): number => (a.sortIndex > b.sortIndex ? 1 : -1);

const getCategories = (
  supportCategories: Array<SupportCategory> = [],
  viewCount: number,
  history: History,
  t: TFunction
): Array<React.ReactElement> => {
  return supportCategories
    .slice()
    .sort(sortBySortIndex)
    .slice(0, viewCount)
    .map(({ title, id, description, iconAsset }: SupportCategory) => {
      return (
        <SupportTile
          key={`support-category-${id}`}
          onClick={(): void => history.push(`/support/${id}`)}
          CategoryHeaderProps={{
            label: t(title),
            icon: iconAsset.src,
            card: true
          }}
        >
          {description ? t(description) : null}
        </SupportTile>
      );
    });
};

const getTopicsWithTitles = (
  topics: Array<SupportTopic> = [],
  t: TFunction,
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void,
  categoryTitle = '',
  iconUrl = ''
): Array<React.ReactElement> => {
  return topics
    .slice()
    .sort(sortBySortIndex)
    .map(({ id, title, description, supportCategories }: SupportTopic) => {
      const searchCategoryTitle = (supportCategories && supportCategories[0]?.title) || '';
      const searchCategoryIconUrl = supportCategories && supportCategories[0]?.iconAsset.src;
      return (
        <SupportTile
          key={`support-topic-${id}`}
          title={t(title)}
          onClick={onClick}
          PaperProps={{ id: id?.toString() }}
          CategoryHeaderProps={{
            label: t(searchCategoryTitle || categoryTitle || ''),
            icon: searchCategoryIconUrl || iconUrl,
            card: true,
            size: 'small'
          }}
        >
          {description}
        </SupportTile>
      );
    });
};

const SupportLayout: React.FC<ModuleProps & SupportLayoutProps> = ({ view, ...restProps }) => {
  const classes = useStyles();
  const { t } = useTranslation(restProps.id);
  const [, { openFullModal, closeModal }] = useStore();
  const history = useHistory();
  const { params } = useRouteMatch<{ categoryId: string }>();
  const [viewCount, setViewCount] = React.useState(4);
  const { categoryId } = params;
  const queryParams = parseQueryParams(history.location?.search);
  const query = getQuery((!queryParams ? view : SupportView.SEARCH) as SupportView);
  const variables = { ...getVariables(queryParams, categoryId, view as SupportView) };
  const { data, error, loading } = useQuery(query, variables);

  const handleViewMore = (): void => {
    if (viewCount < data?.supportCategories?.length || 0) {
      setViewCount(viewCount + 4);
    }
  };

  const openTopicModal = React.useCallback(
    (supportTopicId: string): void => {
      openFullModal({
        title: 'Getting Started',
        body: <TopicDetails onClose={closeModal} />,
        routeAction: 'support-topic',
        routeParams: { supportTopicId }
      });
    },
    [openFullModal, closeModal]
  );

  const handleTopicClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      window.open(
        `/support/${params?.categoryId}${buildSearchQueryParams({
          ...queryParams,
          action: 'support-topic-view',
          'action-supportTopicId': event.currentTarget.id
        })}`,
        '_blank'
      );
    },
    [params, queryParams]
  );

  const getPageTiles = React.useCallback((): Array<React.ReactElement> => {
    return view === SupportView.CATEGORY && !queryParams
      ? getCategories(data?.supportCategories, viewCount, history, t)
      : getTopicsWithTitles(
          !queryParams ? data?.supportCategory.supportTopics : data?.supportTopics,
          t,
          handleTopicClick,
          data?.supportCategory?.title,
          data?.supportCategory?.iconAsset.src
        );
  }, [data, t, viewCount, history, handleTopicClick, queryParams, view]);
  const tiles = getPageTiles();
  useRouteAction(({ action, params }) => {
    if (!action) return;
    switch (action) {
      case 'support-topic':
        return openTopicModal(params.supportTopicId);
      default:
        return null;
    }
  });

  return (
    <PageContainer loading={loading} error={error} {...restProps}>
      <HeaderBar {...restProps} />
      <Box className={classes.container}>
        <SupportHeader />
        <SupportFilterBar
          product={queryParams?.product as string}
          search={queryParams?.search as string}
        />
        <Box flex={1}>
          {view === SupportView.TOPIC && !queryParams && (
            <CategoryHeader
              center
              className={classes.spacing}
              label={t(data?.supportCategory.title)}
              icon={data?.supportCategory.iconAsset.src}
            >
              {data?.supportCategory.description ? t(data.supportCategory.description) : null}
            </CategoryHeader>
          )}
          <SupportTileGrid
            className={cx(classes.spacing, {
              [classes.spacingTop]: queryParams || view === SupportView.CATEGORY
            })}
          >
            {tiles.length || !queryParams ? (
              tiles
            ) : (
              <EmptyResultSetMessage message={(queryParams?.search as string) || ''} />
            )}
          </SupportTileGrid>
          {viewCount < (data?.supportCategories?.length || 0) && SupportView.CATEGORY && (
            <ViewMoreButton onClick={handleViewMore}>{t('View More Categories')}</ViewMoreButton>
          )}
        </Box>
        <ContactUsPanel contactUsUrl={config.digitalProductSupportUrl} />
      </Box>
    </PageContainer>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    gap: `${theme.spacing(2)}px`,
    padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
  },
  spacing: {
    marginTop: theme.spacing(0.5)
  },
  spacingTop: {
    marginTop: theme.spacing(3.5)
  }
}));

export default SupportLayout;
